import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "authConfig/authConfig";
import { msalInstance } from "msalInstance";
import { setSessionExpired } from "state/slices/sessionSlice";
import { ID_TOKEN_REFRESH_BEFORE_EXPIRY_MS, SESSION_DURATION_MS } from "utils/constants";
import { readPersistedSessionStartedAt } from "utils/sessionTiming";

// state/store imports the *ServiceApiSlice files (for their RTK Query
// reducers/middleware), which import THIS file for prepareHeaders — a
// top-level `import { store } from "state/store"` here would be a circular
// import that breaks store.js's own module evaluation. Deferred via
// require() inside dispatchSessionExpired instead, resolved at call-time
// (after every module has finished loading) rather than at import-time.
const dispatchSessionExpired = () => {
  const { store } = require("state/store");
  store.dispatch(setSessionExpired(true));
};

// Every API call (see prepareHeaders in the *ServiceApiSlice files) calls
// this fresh, so several requests can land in the same tick — without this,
// each would independently call acquireTokenSilent and, if it ever fails,
// independently show its own toast. Sharing one in-flight promise means a
// burst of concurrent requests near the token boundary triggers exactly one
// silent-renewal attempt (and, on failure, exactly one session-expired
// dispatch) instead of N.
let inFlightTokenPromise = null;

// Safety net against a refresh loop: after any forced refresh, don't make another
// one for this long, even if the token we got back still looks close to expiry
// (e.g. a short token-lifetime policy, or clock skew between this browser and
// Azure AD).
const FORCED_REFRESH_MIN_INTERVAL_MS = 60 * 1000;
let lastForcedRefreshAt = 0;

const idTokenExpiresSoon = (response) => {
  const exp = response?.idTokenClaims?.exp; // seconds since epoch
  if (ID_TOKEN_REFRESH_BEFORE_EXPIRY_MS <= 0 || typeof exp !== "number") return false;
  return exp * 1000 - Date.now() < ID_TOKEN_REFRESH_BEFORE_EXPIRY_MS;
};

// `forceRefresh` makes MSAL skip its cache and go back to Azure AD for a brand-new
// token set. Without it, acquireTokenSilent hands back the cached ID token for as
// long as the cached *access* token (scope User.Read, which can outlive the 1-hour
// ID token) is still valid — so a token that is minutes from expiring keeps being
// sent to the API even though the session clock was just reset.
//
// Even without forceRefresh, a returned ID token that is about to expire gets ONE
// extra forced refresh. That extra step can never make things worse than before:
// if it fails for any reason, the token we already had is used as-is.
const acquireTokenSilently = async (forceRefresh = false) => {
  const accounts = msalInstance.getAllAccounts();
  const activeAccount = msalInstance.getActiveAccount();

  if (!activeAccount && accounts.length === 0) {
    // User is not signed in. Do not attempt to log a user in outside of the
    // context of MsalProvider.
    return null;
  }

  const account = accounts[0];
  const accessTokenRequest = { ...loginRequest, account, ...(forceRefresh ? { forceRefresh: true } : {}) };

  try {
    const accessTokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest);
    if (forceRefresh) lastForcedRefreshAt = Date.now();

    if (
      !forceRefresh &&
      idTokenExpiresSoon(accessTokenResponse) &&
      Date.now() - lastForcedRefreshAt >= FORCED_REFRESH_MIN_INTERVAL_MS
    ) {
      // Recorded before the attempt, so a failing refresh isn't retried on every request either.
      lastForcedRefreshAt = Date.now();
      try {
        const refreshed = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
        return refreshed?.idToken ?? accessTokenResponse.idToken;
      } catch (refreshError) {
        return accessTokenResponse.idToken;
      }
    }

    return accessTokenResponse.idToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError || e?.subError === "bad_token") {
      dispatchSessionExpired();
    }
    return null;
  }
};

export const getActiveDirectoryAuthToken = async ({ forceRefresh = false } = {}) => {
  // Hard ceiling on session length, independent of whether Azure AD would
  // otherwise keep silently renewing the token forever (acquireTokenSilent
  // succeeds as long as the underlying AAD SSO session is alive, regardless
  // of how long ago THIS app's session started) — see SESSION_DURATION_MS.
  // Checked BEFORE attempting any renewal so a request made past the
  // ceiling fails fast client-side (routing straight to the session-expired
  // flow) instead of silently getting a fresh token and continuing forever.
  const sessionStartedAt = readPersistedSessionStartedAt();
  if (sessionStartedAt != null && Date.now() - sessionStartedAt >= SESSION_DURATION_MS) {
    dispatchSessionExpired();
    return null;
  }

  // A forced refresh never piggybacks on a normal in-flight request (which may be
  // about to return the old cached token); it starts its own, and ordinary
  // requests that arrive while it runs then share its fresh result.
  if (forceRefresh || !inFlightTokenPromise) {
    const tracked = acquireTokenSilently(forceRefresh).finally(() => {
      if (inFlightTokenPromise === tracked) inFlightTokenPromise = null;
    });
    inFlightTokenPromise = tracked;
  }
  return inFlightTokenPromise;
};
